import React from "react";

import {App} from "@capacitor/app";
import {PushNotifications} from "@capacitor/push-notifications";

import {getAppInfo} from "@client/hardware";
import {useAppDispatch} from "@client/store";
import {server} from "@reactivated";

export type PushNotificationStatus = "granted" | "denied" | "prompt";

type Profile = server.core.rpc.Profile.output;

/**
 * Hook that provides push notification status and registration functionality.
 * Returns the current permission status (kept up to date) and a register function.
 * Only works on iOS; returns null status on other platforms.
 *
 * When granted, automatically updates profile settings to mark notifications as enabled.
 */
export function usePushNotifications(options: {profile: Profile}) {
    const [status, setStatus] = React.useState<PushNotificationStatus | null>(null);
    const dispatch = useAppDispatch();

    const markEnabled = React.useCallback(
        (deviceUUID: string) => {
            const {profile} = options;

            if (
                profile.settings.by_device?.[deviceUUID]?.push_notifications?.status ===
                "ENABLED"
            ) {
                return;
            }

            const updatedSettings: Profile["settings"] = {
                ...profile.settings,
                by_device: {
                    ...profile.settings.by_device,
                    [deviceUUID]: {
                        ...profile.settings.by_device?.[deviceUUID],
                        push_notifications: {
                            status: "ENABLED",
                        },
                    },
                },
            };

            dispatch({
                type: "SET_PROFILE",
                profile: {...profile, settings: updatedSettings},
            });
            void server.core.rpc.update_settings(updatedSettings);
        },
        [dispatch, options],
    );

    React.useEffect(() => {
        void (async () => {
            const {platform, deviceUUID} = await getAppInfo();
            if (platform !== "ios") {
                return;
            }
            const result = await PushNotifications.checkPermissions();
            const currentStatus = result.receive as PushNotificationStatus;
            setStatus(currentStatus);

            if (currentStatus === "granted") {
                markEnabled(deviceUUID);
            }
        })();
    }, [markEnabled]);

    const register = React.useCallback(async (): Promise<{
        status: PushNotificationStatus;
        deviceUUID: string;
    }> => {
        const analytics = await import("@client/shared/analytics");

        void analytics.track("CLICKED_ENABLE_PUSH_NOTIFICATIONS");

        // Remove existing listeners to prevent duplicates on repeated calls
        await PushNotifications.removeAllListeners();

        const {deviceUUID: device_id} = await getAppInfo();

        const registrationPromise = new Promise<void>((resolve, reject) => {
            void PushNotifications.addListener("registration", (token) => {
                void (async () => {
                    const {id: application_id} = await App.getInfo();

                    const result = await server.core.rpc.register_apns_device({
                        registration_id: token.value,
                        device_id: device_id,
                        application_id,
                    });

                    if (result.type !== "success") {
                        console.error(
                            "Push notification registration API error:",
                            {registration_id: token.value, device_id},
                            result,
                        );
                        reject(new Error("Push notification registration API error"));
                        return;
                    }

                    void analytics.track("REGISTERED_PUSH_NOTIFICATIONS");
                    resolve();
                })();
            });

            void PushNotifications.addListener("registrationError", () => {
                reject(new Error("Push notification registration error"));
            });
        });

        const permissionResult = await PushNotifications.requestPermissions();
        const newStatus = permissionResult.receive as PushNotificationStatus;

        if (newStatus === "granted") {
            void analytics.track("GRANTED_PUSH_NOTIFICATIONS");
            await PushNotifications.register();
            await registrationPromise;
            markEnabled(device_id);
        } else {
            void analytics.track("DENIED_PUSH_NOTIFICATIONS");
        }

        setStatus(newStatus);

        return {status: newStatus, deviceUUID: device_id};
    }, [markEnabled]);

    return {status, register};
}
